import React, { useEffect, useState } from "react";
import { Check, Moon, Settings2, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { loadProfileLocal } from "@/components/assessment/assessmentScoring";
import {
  DEFAULT_ROUTINE_SETTINGS,
  loadRoutineSettings,
  saveRoutineSettings,
} from "@/components/daily/routineSettings";

const availabilityOptions = [
  { key: "2-5", label: "2–5 min" },
  { key: "5-10", label: "5–10 min" },
  { key: "10-20", label: "10–20 min" },
  { key: "20-30", label: "20–30 min" },
];

const CompanionRoutineSettings = () => {
  const location = useLocation();
  const initial = loadRoutineSettings();
  const initialProfileReady = Boolean(loadProfileLocal());
  const [open, setOpen] = useState(() => initialProfileReady && !initial);
  const [saved, setSaved] = useState(Boolean(initial));
  const [bedtime, setBedtime] = useState(
    initial?.bedtime || DEFAULT_ROUTINE_SETTINGS.bedtime,
  );
  const [bedtimeAvailability, setBedtimeAvailability] = useState(
    initial?.bedtimeAvailability || DEFAULT_ROUTINE_SETTINGS.bedtimeAvailability,
  );
  const [profileReady, setProfileReady] = useState(initialProfileReady);
  const [companionActive, setCompanionActive] = useState(initialProfileReady);

  useEffect(() => {
    const profileSaved = () => setProfileReady(Boolean(loadProfileLocal()));
    const profileCleared = () => {
      setProfileReady(false);
      setCompanionActive(false);
      setOpen(false);
    };
    const companionOpened = () => {
      setCompanionActive(true);
      if (!loadRoutineSettings()) setOpen(true);
    };
    window.addEventListener("adaptive-profile-saved", profileSaved);
    window.addEventListener("adaptive-profile-cleared", profileCleared);
    window.addEventListener("adaptive-companion-opened", companionOpened);
    return () => {
      window.removeEventListener("adaptive-profile-saved", profileSaved);
      window.removeEventListener("adaptive-profile-cleared", profileCleared);
      window.removeEventListener("adaptive-companion-opened", companionOpened);
    };
  }, []);

  if (location.pathname !== "/app" || !profileReady || !companionActive) return null;

  const save = () => {
    saveRoutineSettings({ bedtime, bedtimeAvailability });
    setSaved(true);
    setOpen(false);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed left-4 z-[45] inline-flex items-center gap-2 rounded-full px-4 py-3 text-xs shadow-lg forr-panel-information forr-text-teal-deep sm:left-6"
        style={{ bottom: "calc(5.7rem + env(safe-area-inset-bottom))" }}
      >
        <Settings2 size={15} /> {saved ? "Routine settings" : "Set bedtime routine"}
      </button>
    );
  }

  return (
    <aside
      className="fixed inset-x-4 z-[95] mx-auto max-h-[74vh] max-w-xl overflow-y-auto rounded-[26px] p-5 shadow-2xl forr-panel-information sm:p-6"
      style={{ bottom: "calc(5.7rem + env(safe-area-inset-bottom))" }}
      aria-label="Companion routine settings"
    >
      <button
        type="button"
        onClick={() => setOpen(false)}
        className="absolute right-4 top-4 rounded-full bg-white p-2 forr-text-mute"
        aria-label="Close routine settings"
      >
        <X size={16} />
      </button>
      <div className="flex items-start gap-3 pr-8">
        <Moon size={22} className="mt-1 shrink-0 forr-text-teal-deep" />
        <div>
          <div className="forr-overline">Separate sleep-support settings</div>
          <h2 className="mt-2 font-display text-2xl forr-text-ink">
            When should bedtime support happen?
          </h2>
          <p className="mt-2 text-sm leading-relaxed forr-text-soft">
            These settings schedule the evening and bedtime routine. They do not change the
            state-matched main intervention.
          </p>
        </div>
      </div>
      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <label className="text-sm forr-text-soft">
          Usual bedtime
          <input
            type="time"
            value={bedtime}
            onChange={(event) => setBedtime(event.target.value)}
            className="mt-2 block w-full rounded-xl border border-black/10 bg-white p-3 forr-text-ink"
          />
        </label>
        <div>
          <div className="text-sm forr-text-soft">Time available near bedtime</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {availabilityOptions.map((option) => (
              <button
                type="button"
                key={option.key}
                onClick={() => setBedtimeAvailability(option.key)}
                className="rounded-full px-3.5 py-2 text-xs"
                style={{
                  backgroundColor:
                    bedtimeAvailability === option.key
                      ? "var(--forr-teal-soft)"
                      : "var(--forr-surface)",
                  border: `1px solid ${
                    bedtimeAvailability === option.key
                      ? "var(--forr-teal)"
                      : "var(--forr-line-soft)"
                  }`,
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={save}
        className="mt-6 inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-medium forr-btn-primary"
      >
        <Check size={16} /> Save routine settings
      </button>
    </aside>
  );
};

export default CompanionRoutineSettings;
